import { inject, Injectable } from '@angular/core'
import BaseService from '@ui/Services/BaseService'
import Order from '@client/Order'
import { GuestService } from '@client/Services/GuestService'
import OrderItem from '@client/OrderItem'
import { createOrder } from '@client/utils'
import { PaymentPlan } from '@client/PaymentPlan'
import { PaymentPlanInstalment } from '@client/PaymentPlanInstalment'
import Payment from '@client/Payment'

@Injectable()
export class OrdersService extends BaseService<Order> {
	protected endpoint = '/api/orders'

	private readonly guestsService: GuestService

	constructor() {
		super()
		this.filterable = createOrder()
		this.guestsService = inject(GuestService)
	}

	public findById = async (id: number): Promise<Order> => {
		const response = await this.get({
			url: `${this.endpoint}/${id}`,
		})

		return this.mapToObject(response)
	}

	public mapToOrderItem = (json): OrderItem => {
		return new OrderItem(
			json.id,
			json.unit_amount,
			this.guestsService.mapToObject(json.guest),
		)
	}

	public mapPaymentPlanInstalment = (json): PaymentPlanInstalment => {
		return new PaymentPlanInstalment(
			json.id,
			json.amount,
			new Date(json.due_date),
		)
	}

	public mapPaymentPlans = (json): PaymentPlan => {
		return new PaymentPlan(
			json.id,
			json.currency,
			json.service,
			json.service_id,
			json.total_amount,
			json.instalments.map(this.mapPaymentPlanInstalment),
		)
	}

	public mapPayment = (json): Payment => {
		return new Payment(
			json.id,
			json.currency,
			json.amount,
			new Date(json.charge_date),
			json.paid,
			json.service,
			json.service_id,
			json.billing_request
				? { service_id: json.billing_request.service_id }
				: null,
		)
	}

	public mapToObject = (json): Order => {
		return new Order(
			json.id,
			json.status,
			json.currency,
			json.total,
			json.items.map(this.mapToOrderItem),
			json.currency_precision,
			json.amount_paid,
			json.payment_plans ? json.payment_plans.map(this.mapPaymentPlans) : [],
			json.payments ? json.payments.map(this.mapPayment) : [],
			json.reference,
		)
	}
}
